import polygonClipping from "polygon-clipping";
import type { MultiPolygon, Point, Polygon, Rect } from "@/src/types/map";

const closeRing = (points: Point[]): Point[] => {
  const first = points[0];
  const last = points.at(-1);

  if (!first || !last || (first[0] === last[0] && first[1] === last[1])) {
    return points;
  }

  return [...points, first];
};

export const normaliseRect = (start: Point, end: Point): Rect => ({
  x: Math.min(start[0], end[0]),
  y: Math.min(start[1], end[1]),
  width: Math.abs(end[0] - start[0]),
  height: Math.abs(end[1] - start[1]),
});

export const rectToPolygon = (rect: Rect): Polygon => [
  closeRing([
    [rect.x, rect.y],
    [rect.x + rect.width, rect.y],
    [rect.x + rect.width, rect.y + rect.height],
    [rect.x, rect.y + rect.height],
  ]),
];

export const pointsToPolygon = (points: Point[]): Polygon => [closeRing(points)];

export const ellipseToPolygon = (rect: Rect, segments = 48): Polygon => {
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const radiusX = rect.width / 2;
  const radiusY = rect.height / 2;
  const points: Point[] = [];

  for (let index = 0; index < segments; index += 1) {
    const angle = (index / segments) * Math.PI * 2;
    points.push([
      centerX + Math.cos(angle) * radiusX,
      centerY + Math.sin(angle) * radiusY,
    ]);
  }

  return pointsToPolygon(points);
};

export const hexagonToPolygon = (rect: Rect): Polygon => {
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const radiusX = rect.width / 2;
  const radiusY = rect.height / 2;
  const points: Point[] = [];

  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2;
    points.push([
      centerX + Math.cos(angle) * radiusX,
      centerY + Math.sin(angle) * radiusY,
    ]);
  }

  return pointsToPolygon(points);
};

export const corridorToPolygon = (
  start: Point,
  end: Point,
  width: number,
): Polygon => {
  const deltaX = end[0] - start[0];
  const deltaY = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaY);
  if (length === 0) {
    return rectToPolygon({
      x: start[0] - width / 2,
      y: start[1] - width / 2,
      width,
      height: width,
    });
  }

  const normalX = (-deltaY / length) * (width / 2);
  const normalY = (deltaX / length) * (width / 2);
  return pointsToPolygon([
    [start[0] + normalX, start[1] + normalY],
    [end[0] + normalX, end[1] + normalY],
    [end[0] - normalX, end[1] - normalY],
    [start[0] - normalX, start[1] - normalY],
  ]);
};

export const addPolygon = (
  existing: MultiPolygon,
  polygon: Polygon,
): MultiPolygon => {
  if ((polygon[0]?.length ?? 0) < 4) return existing;
  return existing.length === 0
    ? [polygon]
    : (polygonClipping.union(existing, polygon) as MultiPolygon);
};

export const subtractPolygon = (
  existing: MultiPolygon,
  polygon: Polygon,
): MultiPolygon => {
  if (existing.length === 0 || (polygon[0]?.length ?? 0) < 4) return existing;
  return polygonClipping.difference(existing, polygon) as MultiPolygon;
};

export const addRoom = (existing: MultiPolygon, rect: Rect): MultiPolygon => {
  if (rect.width <= 0 || rect.height <= 0) return existing;
  return addPolygon(existing, rectToPolygon(rect));
};

export const subtractRoom = (
  existing: MultiPolygon,
  rect: Rect,
): MultiPolygon => {
  if (existing.length === 0 || rect.width <= 0 || rect.height <= 0) {
    return existing;
  }

  return subtractPolygon(existing, rectToPolygon(rect));
};

export const geometryBounds = (geometry: MultiPolygon) => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  geometry.forEach((polygon) =>
    polygon.forEach((ring) =>
      ring.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }),
    ),
  );

  return Number.isFinite(minX)
    ? { minX, minY, maxX, maxY }
    : { minX: 0, minY: 0, maxX: 500, maxY: 500 };
};
